import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CLIENT_MAIN_NOTE_MAX_LENGTH,
  type ClientMainNote,
  type NoteVisibility,
} from '@crm/shared';
import { extractApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';
import {
  useClientMainNote,
  useClientMainNoteDelete,
  useClientMainNoteUpsert,
} from './mainNoteApi';

type Props = {
  clientId: number;
  disabled?: boolean; // np. dla zanonimizowanego klienta
};

/**
 * Sekcja głównej notatki klienta (Faza 18) — widok detalu klienta.
 *
 * Widok pasywny: pokazuje treść, autora pierwszego zapisu i ostatniego
 * edytora (z datą). Tryb edycji odsłaniany przyciskiem; visibility default
 * TEAM (decyzja: standardowy use-case to "widoczna dla wszystkich"). Przy
 * zmianie na EMPLOYEE pokazujemy ostrzeżenie że ukrycie odbierze dostęp
 * pozostałym członkom zespołu (sprzeczne z celem "widoczna przy obsłudze").
 */
export function ClientMainNoteSection({ clientId, disabled = false }: Props): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: note, isLoading, error } = useClientMainNote(clientId);
  const upsert = useClientMainNoteUpsert(clientId);
  const del = useClientMainNoteDelete(clientId);

  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<NoteVisibility>('TEAM');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    if (note) {
      setBody(note.body);
      setVisibility(note.visibility);
    } else {
      setBody('');
      setVisibility('TEAM');
    }
  }, [note]);

  if (isLoading) {
    return (
      <div className="rounded-xl bg-white p-5 text-sm text-zinc-400 shadow-sm ring-1 ring-zinc-200">
        {t('common.loading')}
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl bg-red-50 p-5 text-sm text-red-700 ring-1 ring-red-200">
        {extractApiError(error)}
      </div>
    );
  }

  // RBAC po stronie UI (defensywny — backend i tak weryfikuje):
  // - notatka istnieje, visibility=EMPLOYEE i user ≠ autor, ≠ ADMIN → backend
  //   zwróci null, więc tu nie wpadniemy. Bezpieczne.
  // - delete: autor + ADMIN.
  const canDelete =
    !!note && (user?.role === 'ADMIN' || note.author.id === user?.id);

  const onSave = async () => {
    setErrMsg(null);
    try {
      await upsert.mutateAsync({ body: body.trim(), visibility });
      setEditing(false);
    } catch (err) {
      setErrMsg(extractApiError(err));
    }
  };

  const onDelete = async () => {
    if (!window.confirm(t('clientNote.deleteConfirm'))) return;
    setErrMsg(null);
    try {
      await del.mutateAsync();
      setEditing(false);
    } catch (err) {
      setErrMsg(extractApiError(err));
    }
  };

  const onCancel = () => {
    if (note) {
      setBody(note.body);
      setVisibility(note.visibility);
    } else {
      setBody('');
      setVisibility('TEAM');
    }
    setEditing(false);
    setErrMsg(null);
  };

  const dateFmt = new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  return (
    <section
      aria-label={t('clientNote.sectionTitle')}
      className="rounded-xl bg-amber-50 p-5 shadow-sm ring-1 ring-amber-200"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <span aria-hidden>📌</span>
            {t('clientNote.sectionTitle')}
            {note && note.visibility === 'EMPLOYEE' && (
              <span
                className="rounded bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-900"
                title={t('clientNote.visibilityEmployeeHint')}
              >
                🔒 {t('clientNote.visibility.EMPLOYEE')}
              </span>
            )}
          </h2>
          <p className="mt-0.5 text-xs text-amber-800/80">
            {t('clientNote.sectionSubtitle')}
          </p>
        </div>
        {!editing && !disabled && (
          <button
            type="button"
            className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-amber-700"
            onClick={() => setEditing(true)}
          >
            {note ? t('common.actions.edit') : t('clientNote.add')}
          </button>
        )}
      </div>

      {errMsg && (
        <div className="mt-3 rounded-md bg-red-100 px-3 py-2 text-sm text-red-800 ring-1 ring-red-200">
          {errMsg}
        </div>
      )}

      {editing ? (
        <div className="mt-4 space-y-3">
          <textarea
            className="input min-h-[140px] w-full bg-white"
            value={body}
            maxLength={CLIENT_MAIN_NOTE_MAX_LENGTH}
            placeholder={t('clientNote.placeholder')}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="flex items-center justify-between text-xs text-amber-800/80">
            <span>
              {body.length} / {CLIENT_MAIN_NOTE_MAX_LENGTH}
            </span>
          </div>

          <fieldset className="space-y-1">
            <legend className="text-xs font-medium uppercase tracking-wide text-amber-900">
              {t('clientNote.visibilityLabel')}
            </legend>
            <label className="flex cursor-pointer items-start gap-2 text-sm text-amber-950">
              <input
                type="radio"
                name="visibility"
                value="TEAM"
                checked={visibility === 'TEAM'}
                onChange={() => setVisibility('TEAM')}
                className="mt-0.5"
              />
              <div>
                <div className="font-medium">{t('clientNote.visibility.TEAM')}</div>
                <div className="text-xs text-amber-800/80">
                  {t('clientNote.visibility.teamHint')}
                </div>
              </div>
            </label>
            <label className="flex cursor-pointer items-start gap-2 text-sm text-amber-950">
              <input
                type="radio"
                name="visibility"
                value="EMPLOYEE"
                checked={visibility === 'EMPLOYEE'}
                onChange={() => setVisibility('EMPLOYEE')}
                className="mt-0.5"
              />
              <div>
                <div className="font-medium">🔒 {t('clientNote.visibility.EMPLOYEE')}</div>
                <div className="text-xs text-amber-800/80">
                  {t('clientNote.visibility.employeeHint')}
                </div>
              </div>
            </label>
          </fieldset>

          {visibility === 'EMPLOYEE' && (
            <div className="rounded-md bg-amber-100 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-300">
              ⚠️ {t('clientNote.privateWarning')}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
            {note && canDelete && (
              <button
                type="button"
                onClick={() => void onDelete()}
                disabled={del.isPending}
                className="mr-auto rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm text-red-700 transition hover:bg-red-50 disabled:opacity-50"
              >
                {del.isPending ? t('common.saving') : t('common.actions.delete')}
              </button>
            )}
            <button type="button" className="btn-secondary" onClick={onCancel}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={upsert.isPending || body.trim().length === 0}
              onClick={() => void onSave()}
            >
              {upsert.isPending ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      ) : note ? (
        <>
          <p className="mt-3 whitespace-pre-wrap text-sm text-amber-950">{note.body}</p>
          <NoteMeta note={note} dateFmt={dateFmt} t={t} />
        </>
      ) : (
        <p className="mt-3 text-sm text-amber-800/80">{t('clientNote.empty')}</p>
      )}
    </section>
  );
}

function NoteMeta({
  note,
  dateFmt,
  t,
}: {
  note: ClientMainNote;
  dateFmt: Intl.DateTimeFormat;
  t: (key: string, opts?: Record<string, unknown>) => string;
}): ReactNode {
  const editorName = `${note.lastEditedBy.firstName} ${note.lastEditedBy.lastName}`;
  const authorName = `${note.author.firstName} ${note.author.lastName}`;
  const editedAt = dateFmt.format(new Date(note.lastEditedAt));
  return (
    <div className="mt-3 border-t border-amber-200 pt-2 text-xs text-amber-800/90">
      <div>
        {t('clientNote.lastEditedBy', { name: editorName, date: editedAt })}
      </div>
      {note.author.id !== note.lastEditedBy.id && (
        <div className="text-amber-700/80">
          {t('clientNote.authoredBy', { name: authorName })}
        </div>
      )}
    </div>
  );
}
